import React, { Component } from 'react'
import {
  Form,
  Checkbox,
  Select,
  Table,
  Button,
  Icon,
  Tooltip,
  message,
  Modal
} from 'antd'
import api from '../../services/api'

const { Option } = Select

class TranslationSection extends Component {
  constructor(props) {
    super(props)
    this.state = {
      previewingVoiceId: null,
      previewProgress: 0,
      isPreviewGenerating: false
    }
    this.previewAudio = null
    this.activePreviewToken = 0
    this.previewCache = {}
  }

  componentDidUpdate(prevProps) {
    const providerChanged = prevProps.selectedProvider !== this.props.selectedProvider
    const audioToggleOff = prevProps.isGenerateAudio && !this.props.isGenerateAudio
    const langChanged = prevProps.selectedLanguageCode !== this.props.selectedLanguageCode
    if (providerChanged || audioToggleOff || langChanged) {
      this.stopPreview()
    }
  }

  componentWillUnmount() {
    this.stopPreview(true)
  }

  stopPreview = (skipState = false) => {
    this.activePreviewToken += 1

    if (this.previewAudio) {
      this.previewAudio.pause()
      this.previewAudio.currentTime = 0
      this.previewAudio = null
    }

    if (!skipState) {
      this.setState({
        previewingVoiceId: null,
        previewProgress: 0,
        isPreviewGenerating: false
      })
    }
  }

  getLanguageName = languageCode => {
    const { langOptions } = this.props
    const match = langOptions.find(item => item.code === languageCode)
    if (match && match.name) return match.name.toLowerCase()
    return languageCode || ''
  }

  pollTaskStatus = async (taskId, token, delay = 1200, onProgress) => {
    while (token === this.activePreviewToken) {
      const statusResponse = await api.aiVideoGeneration.getTaskStatus(taskId)
      const statusData = statusResponse && statusResponse.data
      const statusValue =
        statusData && statusData.status ? String(statusData.status).toLowerCase() : ''

      if (typeof onProgress === 'function') {
        onProgress(statusData)
      }

      if (
        statusValue === 'success' ||
        statusValue === 'completed' ||
        statusValue === 'done'
      ) {
        return statusData
      }

      if (statusValue === 'failed' || statusValue === 'error') {
        throw new Error('Task failed')
      }

      await new Promise(resolve => setTimeout(resolve, delay))
    }

    throw new Error('Preview cancelled')
  }

  playAudioUrl = (previewUrl, voiceId, token) => {
    const { t } = this.props
    const audio = new Audio(previewUrl)
    this.previewAudio = audio
    audio.addEventListener('timeupdate', () => {
      if (token !== this.activePreviewToken) return
      const duration = audio.duration
      const current = audio.currentTime
      if (isFinite(duration) && duration > 0) {
        this.setState({
          previewProgress: Math.min(100, (current / duration) * 100)
        })
      }
    })
    audio.addEventListener('ended', () => {
      if (token !== this.activePreviewToken) return
      this.setState({
        previewingVoiceId: null,
        previewProgress: 0,
        isPreviewGenerating: false
      })
      this.previewAudio = null
    })
    audio.addEventListener('error', () => {
      if (token !== this.activePreviewToken) return
      this.stopPreview()
      message.warning(t('v4:voice_preview_not_available'))
    })
    this.setState({
      previewingVoiceId: voiceId,
      previewProgress: 0,
      isPreviewGenerating: false
    })
    audio.play().catch(() => {
      if (token !== this.activePreviewToken) return
      this.stopPreview()
      message.warning(t('v4:voice_preview_not_available'))
    })
  }

  handlePreviewElevenLabs = voice => {
    const { t, selectedLanguageCode } = this.props
    let previewUrl =
      voice.preview_url ||
      (Array.isArray(voice.verified_languages) &&
        voice.verified_languages[0] &&
        voice.verified_languages[0].preview_url)

    if (
      selectedLanguageCode &&
      Array.isArray(voice.verified_languages) &&
      voice.verified_languages.length
    ) {
      const langMatch = voice.verified_languages.find(langObj => {
        if (typeof langObj === 'string') return langObj === selectedLanguageCode
        return (
          langObj &&
          (langObj.language === selectedLanguageCode ||
            langObj.code === selectedLanguageCode)
        )
      })
      if (langMatch && langMatch.preview_url) {
        previewUrl = langMatch.preview_url
      }
    }

    if (!previewUrl) {
      message.warning(t('v4:voice_preview_not_available'))
      return
    }

    this.stopPreview()
    const token = this.activePreviewToken
    this.playAudioUrl(previewUrl, voice.voice_id, token)
  }

  handlePreviewSharelook = async voice => {
    const { t, courseId, selectedLanguageCode } = this.props
    const cacheKey = `${selectedLanguageCode || 'en'}::${voice.voice_id}`
    const cachedPreviewUrl = this.previewCache[cacheKey]
    if (cachedPreviewUrl) {
      this.stopPreview()
      const token = this.activePreviewToken
      this.playAudioUrl(cachedPreviewUrl, voice.voice_id, token)
      return
    }

    this.stopPreview()
    const token = this.activePreviewToken

    this.setState({
      previewingVoiceId: voice.voice_id,
      previewProgress: 5,
      isPreviewGenerating: true
    })

    try {
      const synthResponse = await api.aiVideoGeneration.synthesizeVoicePreview({
        video_id: courseId,
        language: this.getLanguageName(selectedLanguageCode),
        voice_name:
          voice.voice_id || voice.name || voice.description || 'default_preview_voice'
      })

      if (token !== this.activePreviewToken) return

      const synthData = synthResponse && synthResponse.data ? synthResponse.data : {}
      const taskId =
        synthData.task_id ||
        synthData.taskId ||
        synthData.id ||
        (synthData.output && synthData.output.task_id)
      if (!taskId) throw new Error('Missing preview task id')

      const taskData = await this.pollTaskStatus(taskId, token, 1200, statusData => {
        if (token !== this.activePreviewToken) return
        const progressValue =
          statusData && typeof statusData.progress === 'number'
            ? statusData.progress
            : null
        if (progressValue !== null) {
          this.setState({
            previewProgress: Math.max(5, Math.min(95, progressValue))
          })
        } else {
          this.setState(prev => ({
            previewProgress: Math.min(95, prev.previewProgress + 5)
          }))
        }
      })

      if (token !== this.activePreviewToken) return

      const output = taskData && taskData.output ? taskData.output : {}
      const previewUrl = output.audio_url || output.url || output.output
      if (!previewUrl) throw new Error('Preview url not available')
      this.previewCache[cacheKey] = previewUrl

      this.setState({
        previewProgress: 98
      })
      this.playAudioUrl(previewUrl, voice.voice_id, token)
    } catch (error) {
      if (token !== this.activePreviewToken) return
      this.stopPreview()
      message.warning(t('v4:voice_preview_not_available'))
    }
  }

  handlePreviewVoice = voice => {
    const { selectedProvider } = this.props
    const { previewingVoiceId, isPreviewGenerating } = this.state

    if (isPreviewGenerating && previewingVoiceId && previewingVoiceId !== voice.voice_id) {
      return
    }

    if (previewingVoiceId === voice.voice_id) {
      if (isPreviewGenerating) {
        Modal.confirm({
          centered: true,
          title: 'Stop Preview Generation?',
          content:
            'A voice preview is still being generated. Do you want to stop this generation?',
          okText: 'Stop Generation',
          cancelText: 'Continue',
          onOk: () => this.stopPreview()
        })
      } else {
        this.stopPreview()
      }
      return
    }

    if (selectedProvider === 'generate-tts-v2') {
      this.handlePreviewSharelook(voice)
    } else {
      this.handlePreviewElevenLabs(voice)
    }
  }

  renderPreviewButton = row => {
    const { t } = this.props
    const { previewingVoiceId, previewProgress, isPreviewGenerating } = this.state
    const isCurrent = previewingVoiceId === row.voice_id
    const isGenerating = isCurrent && isPreviewGenerating
    const isPlaying = isCurrent && !isPreviewGenerating
    const percent = Math.round(previewProgress)
    const isDisabled =
      isPreviewGenerating && previewingVoiceId && previewingVoiceId !== row.voice_id

    return (
      <Tooltip title={isGenerating ? `Generating ${percent}%` : t('v4:preview')}>
        <Button
          className={`voice-generate-btn ${isCurrent ? 'active' : ''} ${
            isGenerating ? 'generating' : ''
          }`}
          onClick={e => {
            e.stopPropagation()
            this.handlePreviewVoice(row)
          }}
          disabled={isDisabled}
        >
          <span className="voice-generate-icon">
            <Icon type={isGenerating ? 'loading' : isPlaying ? 'pause' : 'caret-right'} />
          </span>
          <span className="voice-generate-label">
            {isGenerating ? 'Generating' : isPlaying ? 'Playing' : 'Preview'}
          </span>
          <span className="voice-generate-percent">
            {isGenerating ? `${percent}%` : ''}
          </span>
        </Button>
      </Tooltip>
    )
  }

  render() {
    const {
      t,
      getFieldDecorator,
      lang,
      langOptions,
      onLanguageChange,
      isGenerateAudio,
      onGenerateAudioChange,
      selectedProvider,
      onProviderChange,
      voiceTableData,
      isVoicesLoading,
      selectedVoiceId,
      onVoiceSelect
    } = this.props

    const sortedVoices = voiceTableData
      .map(voice => ({ ...voice }))
      .sort((a, b) => {
        const qualityA = typeof a.audio_quality_score === 'number' ? a.audio_quality_score : 0
        const qualityB = typeof b.audio_quality_score === 'number' ? b.audio_quality_score : 0
        if (qualityA !== qualityB) return qualityB - qualityA
        return String(a.name || a.voice_id || '').localeCompare(
          String(b.name || b.voice_id || '')
        )
      })

    const normalizedRows = sortedVoices.map((voice, index) => {
      const isSharelookProvider = selectedProvider === 'generate-tts-v2'
      const hasCleanName =
        voice &&
        voice.name &&
        !String(voice.name).toLowerCase().includes('migrated legacy voice') &&
        !String(voice.name).toLowerCase().includes('voice_id') &&
        !String(voice.name).toLowerCase().includes('voice description')

      const displayName = isSharelookProvider
        ? `Default Voice ${index + 1}`
        : hasCleanName
          ? voice.name
          : `Voice ${index + 1}`

      return {
        ...voice,
        displayIndex: index + 1,
        displayName
      }
    })

    const voiceColumns = [
      {
        title: t('v4:voice_name'),
        dataIndex: 'name',
        key: 'name',
        width: '46%',
        render: (_, row) => {
          const useCases = Array.isArray(row.use_case_tags) ? row.use_case_tags : []
          const tags = useCases.slice(0, 2)
          return (
            <div className="voice-name-cell">
              <div className="voice-name-main" title={row.displayName}>
                {row.displayName}
              </div>
              <div className="voice-meta-line">
                <span>{row.accent || row.language || 'general'}</span>
                <span>{row.pace_label || 'medium pace'}</span>
              </div>
              {!!tags.length && (
                <div className="voice-tag-row">
                  {tags.map(tag => (
                    <span key={`${row.voice_id}-${tag}`} className="voice-tag-chip">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )
        }
      },
      {
        title: 'Gender',
        key: 'gender',
        width: 100,
        render: (_, row) => (
          <span className="voice-gender-cell">
            {row.gender ? String(row.gender) : 'neutral'}
          </span>
        )
      },
      {
        title: t('v4:preview'),
        key: 'preview',
        width: 190,
        render: (_, row) => this.renderPreviewButton(row)
      },
      {
        title: t('v4:select_voice'),
        key: 'select',
        width: 140,
        render: (_, row) => {
          const isSelected = selectedVoiceId === row.voice_id
          return (
            <Button
              className={`voice-select-btn ${isSelected ? 'active' : ''}`}
              onClick={e => {
                e.stopPropagation()
                onVoiceSelect(row.voice_id)
              }}
            >
              <span className={`voice-select-icon ${isSelected ? 'visible' : ''}`}>
                <Icon type="check" />
              </span>
              {isSelected ? t('v4:selected') : t('v4:select')}
            </Button>
          )
        }
      }
    ]

    return (
      <div className="clone-translation-panel">
        <div className="section-head">
          <h3>Translation Settings</h3>
        </div>

        <div className="form-row">
          <label>{t('v4:translateSelectLang')}</label>
          <Form.Item colon={false} required={false}>
            {getFieldDecorator('lang', {
              initialValue: lang
            })(
              <Select
                showSearch
                optionFilterProp="children"
                defaultValue={lang}
                onChange={onLanguageChange}
                className="clone-select"
                dropdownClassName="clone-modal-select-dropdown"
              >
                {langOptions.map(item => (
                  <Option key={item.code} value={item.code}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </div>

        <div className="form-row audio-toggle-row">
          <Form.Item colon={false} required={false}>
            <Checkbox checked={isGenerateAudio} onChange={onGenerateAudioChange}>
              {t('v5:generate_audio')}
            </Checkbox>
          </Form.Item>
        </div>

        {isGenerateAudio && (
          <div className="clone-voice-panel">
            <div className="form-row">
              <label>Provider</label>
              <Form.Item colon={false} required={false}>
                <Select
                  value={selectedProvider}
                  onChange={onProviderChange}
                  className="clone-select"
                  dropdownClassName="clone-modal-select-dropdown"
                >
                  <Option value="generate-tts-v2">Sharelook TTS</Option>
                  <Option value="elevenlabs">Sharelook Premium</Option>
                </Select>
              </Form.Item>
            </div>

            <div className="form-row">
              <label>{t('v4:select_voice')}</label>
              <div className="voice-section-hint">
                Generate preview to inspect quality, then select the voice.
              </div>
              <Form.Item colon={false} required={false}>
                <Table
                  className="clone-voice-table"
                  size="small"
                  columns={voiceColumns}
                  dataSource={normalizedRows}
                  tableLayout="fixed"
                  pagination={false}
                  scroll={{ y: 280 }}
                  loading={isVoicesLoading}
                  rowClassName={row =>
                    row.voice_id === selectedVoiceId ? 'voice-row-selected' : ''
                  }
                  onRow={row => ({
                    onClick: () => onVoiceSelect(row.voice_id)
                  })}
                  locale={{
                    emptyText: t('v4:loading_list')
                  }}
                />
              </Form.Item>
            </div>
          </div>
        )}
      </div>
    )
  }
}

export default TranslationSection
